import { useNavigate } from '@fukuri-admins/hooks/common';
import { TrashIcon } from '@fukuri-admins/ui/common';
import clsx from 'clsx';
import { useState } from 'react';
import { deleteClientAdminUser } from '../../../api/client_admin_users/id';
import { ClientAdminUserType } from '../../../types/resources/ClientAdminUserType';
import { ActionTableColumnsType } from '../../../types/table/ActionTableColumnsType';
import { ActionTableSortParamsType } from '../../../types/table/ActionTableSortParamsType';
import { convertDateTime } from '../../../utils/dateTime';
import { getClientAdminUserLink } from '../../../utils/getLinks';
import ConfirmModal from '../../common/ConfirmModal';
import MessageModal from '../../common/MessageModal';
import { TableRoot } from '../../common/Table/TableBase/TableRoot';
import styles from './styles.module.scss';

interface StagingTableProps {
  columns: ActionTableColumnsType[];
  rows: ClientAdminUserType[];
  sortParams: ActionTableSortParamsType;
  headerSort: (column: string) => void;
}

export const ClientAdminUserTableContent = ({
  columns,
  rows,
  sortParams,
  headerSort,
}: StagingTableProps) => {
  const navigate = useNavigate();

  const [errMsg, setErrMsg] = useState('');
  const [workRow, setWorkRow] = useState<Partial<ClientAdminUserType>>({});

  //------------delete flag function------------
  const [deleteModalStatus, setDeleteModalStatus] = useState(false);

  const handleDeleteModalOpen = async (row: ClientAdminUserType) => {
    setWorkRow(row);
    setDeleteModalStatus(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteClientAdminUser(workRow.id);
      setErrMsg(''); // Clear previous error messages
      setWorkRow({}); // Reset workRow state
      setDeleteModalStatus(false); // Close the delete modal
      window.location.reload(); // Refresh the page
    } catch (error) {
      if (error) {
        setErrMsg(
          `Error deleting user:\n${error.message}\nDetails: ${
            error.details?.join(', ') || 'No additional details provided.'
          }`
        );
      } else {
        setErrMsg('An unexpected error occurred while deleting the user.');
      }
      setErrorMessageModalStatus(true); // Show the error modal
    }
  };

  const handleDeleteModalClose = () => {
    setWorkRow({});
    setDeleteModalStatus(false);
  };

  const [errorMessage2ModalStatus, setErrorMessageModalStatus] = useState(false);

  function handleErrorMessageModal() {
    setErrorMessageModalStatus(false);
  }

  const getNestedValue = (obj: ClientAdminUserType, path: string) => {
    return path.split('.').reduce((acc, part) => acc?.[part], obj);
  };

  const tableRows =
    rows !== undefined ? (
      rows.map((row: ClientAdminUserType, key: number) => {
        return (
          <tr
            key={key}
            className={clsx(styles.tableRow)}
            onClick={() => {
              navigate(`${getClientAdminUserLink(row.id)}`);
            }}
            data-testid={`table-row-${key}`}
          >
            {columns.map((column, columnIndex) => (
              <td
                key={column.key}
                className={clsx(styles.tableCell, styles.greyout, {
                  [styles.clickable]: column.sortable,
                })}
                onClick={() => {
                  if (column.sortable) {
                    headerSort(column.key);
                  }
                }}
                data-testid={`table-cell-${columnIndex === 0 ? 'no' : column.key}`}
              >
                {column.key === 'delete' ? (
                  <div className={styles.action}>
                    <div
                      className={styles.actionIcon}
                      data-testid='delete-user-button'
                      onClick={e => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleDeleteModalOpen(row);
                      }}
                    >
                      <TrashIcon />
                    </div>
                  </div>
                ) : column.key === 'created_at' ? (
                  convertDateTime(getNestedValue(row, column.key))
                ) : (
                  (getNestedValue(row, column.key) ?? '-')
                )}
              </td>
            ))}
          </tr>
        );
      })
    ) : (
      <></>
    );

  return (
    <>
      {rows?.length === 0 || rows === null ? (
        <div className={styles.emptyTable}>データなし</div>
      ) : (
        <TableRoot
          columns={columns}
          rows={tableRows}
          sortParams={sortParams}
          onClickHeader={column => headerSort(column)}
        />
      )}
      <ConfirmModal
        title={`利用者を削除する`}
        btitle='削除'
        description={`「利用者：${workRow?.full_name || '未定義'}」を本当に削除しますか？`}
        show={deleteModalStatus}
        onClose={handleDeleteModalClose}
        action={handleDeleteConfirm}
        data-testid='delete-confirm-modal'
      />
      <MessageModal
        title='エラー'
        description={`エラーが発生しました。\n\n<<エラーメッセージ>>\n${errMsg}`}
        show={errorMessage2ModalStatus}
        onClose={() => setErrorMessageModalStatus(false)}
        action={handleErrorMessageModal}
        data-testid='delete-error-modal'
      />
    </>
  );
};
